import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authLogin, authLogout, authMe, clearFileToken, endImpersonation, exchangeNavixy, getImpersonation, getToken, refreshFileToken, restoreSuperadminSession, setActingTenant, setToken } from "@/lib/api";

const AuthContext = createContext(null);

// session_key Navixy conservé UNIQUEMENT en mémoire (jamais localStorage/sessionStorage/cookie)
let ssoSessionKey = null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [ssoPending, setSsoPending] = useState(false);
  const [ssoError, setSsoError] = useState(null);
  const [ssoUnconfigured, setSsoUnconfigured] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get("session_key") || params.get("hash");
    if (key) {
      params.delete("session_key");
      params.delete("hash");
      const qs = params.toString();
      window.history.replaceState({}, document.title,
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
      setSsoPending(true);
      exchangeNavixy(key)
        .then(async (data) => {
          ssoSessionKey = key;
          setToken(data.token);
          await refreshFileToken().catch(() => {});
          setSsoError(null);
          // Charge utilisateur complète (tenant_modules, bandeau démo) : l'échange SSO ne renvoie qu'un profil minimal
          setUser(await authMe().catch(() => data.user));
        })
        .catch((err) => {
          setToken(null);
          const detail = String(err?.response?.data?.detail || "");
          if (err?.response?.status === 403 && detail.includes("Aucun compte Documents associé")) {
            setSsoUnconfigured(true);
          } else {
            setSsoError(detail || "Session Navixy invalide — reconnectez-vous au hub LOGITRAK.");
          }
          setUser(null);
        })
        .finally(() => setSsoPending(false));
      return;
    }
    if (!getToken()) {
      setUser(null);
      return;
    }
    authMe()
      .then(async (u) => {
        await refreshFileToken().catch(() => {});
        setUser(u);
      })
      .catch(() => {
        setToken(null);
        setUser(null);
      });
  }, []);

  // Quitter le compte : termine la session côté serveur (jeton d'assistance révoqué), restaure le superadmin, retour /admin.
  const exitImpersonation = useCallback(async () => {
    await endImpersonation().catch(() => {});
    clearFileToken();
    restoreSuperadminSession();
    window.location.assign("/admin");
  }, []);

  const logout = useCallback(() => {
    if (getImpersonation()) {
      exitImpersonation();
      return;
    }
    ssoSessionKey = null;
    authLogout()
      .catch(() => {})
      .finally(() => {
        clearFileToken();
        setActingTenant(null);
        setToken(null);
        setUser(null);
        window.location.assign("/login");
      });
  }, [exitImpersonation]);

  // Rafraîchissement silencieux du profil (modules activés par le Super Admin, rôle, périmètre) au retour sur l'onglet
  // et toutes les 5 min : une bascule de module côté console se reflète sans reconnexion (le serveur bloque déjà en 403).
  useEffect(() => {
    if (!user) return undefined;
    const sync = () => authMe().then((u) => setUser((prev) => (JSON.stringify(prev) === JSON.stringify(u) ? prev : u))).catch(() => {});
    const iv = setInterval(() => { refreshFileToken().catch(() => {}); }, 8 * 60 * 1000);
    const ivMe = setInterval(sync, 5 * 60 * 1000);
    const onFocus = () => { refreshFileToken().catch(() => {}); sync(); };
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      clearInterval(ivMe);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  // Heartbeat SSO : re-validation silencieuse de la session Navixy toutes les 10 min.
  // 401/403 (session hub expirée/invalidée) → déconnexion. Erreur réseau → on garde la session.
  useEffect(() => {
    if (!user || !ssoSessionKey) return undefined;
    const iv = setInterval(() => {
      exchangeNavixy(ssoSessionKey)
        .then((d) => setToken(d.token))
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 401 || status === 403) logout();
        });
    }, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user, logout]);

  const login = useCallback(async (email, password) => {
    const data = await authLogin(email, password);
    setToken(data.token);
    await refreshFileToken().catch(() => {});
    setSsoError(null);
    setUser(data.user);
    return data.user;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, exitImpersonation, ssoPending, ssoError, ssoUnconfigured }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
